import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, ICON_NAMES} from '@nur-portal/ui-kit';

import {existsCookie, setCookie} from '../../services/cookie';
import {getHoursToNextDay} from '../../services/dates';

import {BookmarkTooltip} from './bookmark-tooltip';
import {bookmarksService} from './bookmarks-service';
import {
  BOOKMARK_CUSTOM_EVENTS,
  BOOKMARKS_ARTICLE_TOOLTIP_COOKIE,
  THIRTY_DAYS_IN_HOURS
} from './constants';

import styles from './bookmark-add-button.module.css';

const addCookie = (hoursToSetCookie = THIRTY_DAYS_IN_HOURS) => {
  setCookie(BOOKMARKS_ARTICLE_TOOLTIP_COOKIE, true, hoursToSetCookie);
};

const BookmarkAddButton = ({articleId}) => {
  const [isShowModal, setShowModal] = useState(false);
  const [currentBookmarkId, setCurrentBookmarkId] = useState(null);
  const {t} = useTranslation('bookmarks');
  const containerRef = useRef(null);

  const handleClickBookmark = async () => {
    addCookie();
    setShowModal(false);
    let currentBookmarkTemporaryId = null;

    if (currentBookmarkId) {
      bookmarksService.removeBookmark(articleId);
      currentBookmarkTemporaryId = null;
    } else {
      bookmarksService.addBookmark(articleId);
      currentBookmarkTemporaryId = articleId;
    }

    setCurrentBookmarkId(currentBookmarkTemporaryId);

  };

  useEffect(() => {
    const checkBookmarks = async () => {
      try {
        const isHasBookmark = bookmarksService.hasBookmark(articleId);

        setCurrentBookmarkId(isHasBookmark ? articleId : null);
      } catch (error) {
        console.error(error);
        setCurrentBookmarkId(null);
      }
    };

    checkBookmarks();
    document.addEventListener(BOOKMARK_CUSTOM_EVENTS.REMOVE, checkBookmarks);
    return () => document.removeEventListener(BOOKMARK_CUSTOM_EVENTS.REMOVE, checkBookmarks);
  }, [articleId, setCurrentBookmarkId]);

  const handleCloseTooltip = () => {
    setShowModal(false);
    addCookie();
  };

  const handleCloseTooltipAfterAnimation = () => {
    setShowModal(false);
    addCookie(getHoursToNextDay());
  };

  useEffect(() => {
    if (containerRef.current) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !existsCookie(BOOKMARKS_ARTICLE_TOOLTIP_COOKIE)) {
            setShowModal(true);
          }
        });
      }, {treshold: 0.5});

      observer.observe(containerRef.current);

      return () => {
        observer.unobserve(containerRef.current);
      };
    }
  }, [containerRef]);

  return (
    <div className={styles.addBookmarkContainer} ref={containerRef}>
      <div className={styles.wrapper}>
        <button
          className={styles.addBookmarkButton}
          onClick={handleClickBookmark}
        >
          <Icon
            name={currentBookmarkId !== null ? ICON_NAMES.BOOKMARK_ON_24 : ICON_NAMES.BOOKMARK_OFF_24}
            className={styles.bookmarkButtonIcon}
          />
        </button>
        {
          isShowModal &&
          <BookmarkTooltip
            onAnimationEnd={handleCloseTooltipAfterAnimation}
            onClose={handleCloseTooltip}
            className={styles.tooltip}
            containerClassName={styles.tooltipContainer}
            text={t('addBookmarkTooltip')}
          />
        }
      </div>
    </div>
  );
};

export {BookmarkAddButton};
